import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, apiErrorText } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { GoogleLogo, CheckCircle, XCircle, Upload, Trash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [company, setCompany] = useState({
    name: "", document: "", email: "", phone: "", address: "", logo_base64: null,
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const fileRef = useRef();

  const loadCompany = async () => {
    const { data } = await api.get("/company/settings");
    setCompany(data);
  };

  useEffect(() => {
    loadCompany();
    if (params.get("google_connected") === "1") {
      toast.success("Google Calendar conectado com sucesso");
      refresh();
    }
    // eslint-disable-next-line
  }, []);

  const saveCompany = async () => {
    setSavingCompany(true);
    try {
      await api.put("/company/settings", company);
      toast.success("Dados da empresa salvos");
    } catch (e) { toast.error(apiErrorText(e)); }
    finally { setSavingCompany(false); }
  };

  const onLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo deve ter no máximo 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCompany((c) => ({ ...c, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const connect = async () => {
    try {
      const { data } = await api.get("/google/auth-url");
      window.location.href = data.url;
    } catch (e) { toast.error(apiErrorText(e)); }
  };
  const disconnect = async () => {
    await api.post("/google/disconnect");
    toast.success("Desconectado");
    refresh();
  };
  const connected = !!user?.google_connected;

  return (
    <Layout title="Configurações" subtitle="Empresa, integrações e preferências">
      <div className="max-w-4xl space-y-6">
        <div className="tech-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Dados da Empresa</h3>
              <p className="text-sm text-muted-foreground mt-1">Aparecem em contratos e propostas em PDF.</p>
            </div>
            <Button onClick={saveCompany} disabled={savingCompany} data-testid="save-company-btn">
              {savingCompany ? "Salvando..." : "Salvar"}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-3">
              <Label className="label-tech">Logo</Label>
              <div className="w-full aspect-square bg-white rounded-md border border-border flex items-center justify-center overflow-hidden">
                {company.logo_base64 ? (
                  <img src={company.logo_base64} alt="Logo" className="w-full h-full object-contain p-3" />
                ) : (
                  <div className="text-xs text-muted-foreground">Sem logo</div>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogoUpload} data-testid="logo-upload" />
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1 gap-2" onClick={() => fileRef.current?.click()}>
                  <Upload size={14} weight="bold" /> Trocar
                </Button>
                {company.logo_base64 && (
                  <Button variant="outline" size="icon" onClick={() => setCompany((c) => ({ ...c, logo_base64: null }))}>
                    <Trash size={14} className="text-destructive" />
                  </Button>
                )}
              </div>
            </div>

            <div className="md:col-span-2 space-y-4">
              <div className="space-y-2">
                <Label className="label-tech">Razão Social *</Label>
                <Input value={company.name || ""} onChange={(e) => setCompany({ ...company, name: e.target.value })} data-testid="company-name" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="label-tech">CNPJ</Label>
                  <Input value={company.document || ""} onChange={(e) => setCompany({ ...company, document: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label className="label-tech">Telefone</Label>
                  <Input value={company.phone || ""} onChange={(e) => setCompany({ ...company, phone: e.target.value })} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="label-tech">E-mail</Label>
                  <Input type="email" value={company.email || ""} onChange={(e) => setCompany({ ...company, email: e.target.value })} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="label-tech">Endereço</Label>
                  <Textarea rows={2} value={company.address || ""} onChange={(e) => setCompany({ ...company, address: e.target.value })} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="tech-card p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-md bg-accent/10 flex items-center justify-center">
                <GoogleLogo size={24} weight="bold" className="text-accent" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Google Calendar</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-lg">
                  Sincronize suas inspeções com o Google Calendar. Cada nova inspeção criada aqui será replicada na sua agenda Google.
                </p>
                <div className="mt-3 flex items-center gap-2 text-sm">
                  {connected ? (
                    <><CheckCircle size={16} className="text-green-400" weight="fill" /><span className="text-green-400 font-bold">Conectado</span></>
                  ) : (
                    <><XCircle size={16} className="text-muted-foreground" /><span className="text-muted-foreground">Não conectado</span></>
                  )}
                </div>
              </div>
            </div>
            <div>
              {connected ? (
                <Button variant="outline" onClick={disconnect} data-testid="google-disconnect">Desconectar</Button>
              ) : (
                <Button onClick={connect} data-testid="google-connect" className="gap-2">
                  <GoogleLogo size={16} weight="bold" /> Conectar
                </Button>
              )}
            </div>
          </div>
          {!connected && (
            <div className="mt-6 p-4 border border-border rounded-md bg-secondary/30 text-sm">
              <div className="label-tech text-muted-foreground mb-2">Configuração necessária</div>
              <p className="text-muted-foreground">
                Configure <span className="mono text-accent">GOOGLE_CLIENT_ID</span> e{" "}
                <span className="mono text-accent">GOOGLE_CLIENT_SECRET</span> em <span className="mono">backend/.env</span>.
                URL de redirecionamento: <span className="mono text-accent break-all">{process.env.REACT_APP_BACKEND_URL}/api/google/callback</span>
              </p>
            </div>
          )}
        </div>

        <div className="tech-card p-6">
          <h3 className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Conta</h3>
          <div className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">Nome</span><span className="font-bold">{user?.name}</span></div>
            <div className="flex justify-between border-b border-border pb-2"><span className="text-muted-foreground">E-mail</span><span className="font-bold mono">{user?.email}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Função</span><span className="font-bold">{user?.role}</span></div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
